import { Dialog } from '@angular/cdk/dialog';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of, take } from 'rxjs';
import {
  DeviceDetailsDto,
  DeviceDto,
  GeneralInfoItem,
  QsTrinityProDetailsDto,
  QsUavDetailsDto,
} from 'src/app/models/device.model';
import { LicenseBatchDto } from 'src/app/models/license.model';
import {
  allowOpenLogUILinks,
  managesDevicesAcrossCompanies,
  UserRole,
} from 'src/app/models/user.model';
import { DeviceService } from 'src/app/services/device.service';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { environment } from 'src/environments/environment';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';
import { ProfileState } from 'src/app/stores/store.states';
import {
  getDeviceDisplayName,
  getDeviceModelName,
} from 'src/app/utils/device-display';
import { resolveLogUiHardwareRevision } from 'src/app/utils/logui';
import { EditDeviceModalComponent } from '../../components/modals/edit-device-modal/edit-device-modal.component';
import { FeatureFlagsEnum } from '../../feature-flag-constant';

@Component({
  selector: 'app-device-details',
  templateUrl: './device-details.component.html',
  styleUrl: './device-details.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DeviceDetailsComponent implements OnInit {
  private readonly allowedResourceReturnUrls = new Set([
    '/resources',
    '/admin/all-resources',
  ]);
  private readonly deviceImageByModel = [
    {
      models: ['vector'],
      src: '../../../assets/imgs/vector.png',
    },
    {
      models: ['trinity', 'triniti', 'qstrinitypro'],
      src: '../../../assets/imgs/trinity-pro.png',
    },
    {
      models: ['gcs', 'qsgcs'],
      src: '../../../assets/imgs/gcs.png',
    },
    {
      models: ['application', 'app'],
      src: '../../../assets/imgs/app.png',
    },
    {
      models: ['antenna', 'qsantennatracker'],
      src: '../../../assets/imgs/antenna-tracker.png',
    },
    {
      models: ['cuas', 'qscuas', 'skyron'],
      src: '../../../assets/imgs/skyron.png',
    },
  ];
  errorMessage = '';
  device?: DeviceDto;
  deviceId: string | null = null;
  deviceCompanyId: string | null = null;
  linkedLicenseBatches: LicenseBatchDto[] = [];
  generalInfoItems: GeneralInfoItem[] = [];
  devicePropertyItems: GeneralInfoItem[] = [];
  profile$: Observable<ProfileState> | undefined;
  readonly isLicenseManagementEnabled$ =
    this.featureFlagService.isFeatureEnabled(
      FeatureFlagsEnum.LICENSE_MANAGEMENT,
    );
  protected readonly UserRole = UserRole;

  private readonly destroyRef = inject(DestroyRef);

  /**
   * Whether this user may edit the device, mirroring what the backend accepts: someone who
   * manages devices across companies, or a member of the company the device belongs to. A
   * support user reaches every device but may change none of them.
   */
  canEditDevice(profile: ProfileState): boolean {
    return (
      managesDevicesAcrossCompanies(profile) ||
      (!!profile.role && profile.companyId === this.device?.company?.id)
    );
  }

  authorizedLogUiHref(profile: ProfileState | null): string | undefined {
    return allowOpenLogUILinks(profile) ? this.logUiHref : undefined;
  }

  constructor(
    public dialog: Dialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly deviceService: DeviceService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly store: Store,
    private readonly translate: TranslateService,
  ) {
    this.deviceId = this.route.snapshot.paramMap.get('deviceId');
  }

  ngOnInit(): void {
    this.profile$ = this.store.select(selectProfileState);

    if (!this.deviceId) {
      this.backToResourcesList();
      return;
    }

    this.loadDevice(this.deviceId);
  }

  get pageTitle(): string | undefined {
    return getDeviceModelName(this.device) || undefined;
  }

  get pageSubtitle(): string | undefined {
    return getDeviceDisplayName(this.device) || undefined;
  }

  get logUiHref(): string | undefined {
    const baseUrl = environment.logUiUrl.trim();
    const details = this.device?.details;
    if (!baseUrl || !this.isAircraftDeviceDetails(details)) {
      return undefined;
    }

    const hardwareRevision = details.aircraftModel?.hardwareRevision;
    const unitId =
      details.unitId ?? Number(this.device?.deviceSummary?.unitId?.trim());

    if (
      hardwareRevision === undefined ||
      !Number.isSafeInteger(hardwareRevision) ||
      hardwareRevision < 0 ||
      !Number.isSafeInteger(unitId) ||
      unitId < 0
    ) {
      return undefined;
    }

    const logUiHardwareRevision =
      resolveLogUiHardwareRevision(hardwareRevision);

    try {
      const url = new URL('/logui', baseUrl);
      url.searchParams.set(
        'd',
        'ch-' + [logUiHardwareRevision, unitId].join('-') + '@:',
      );
      url.searchParams.set('dv', '2');
      return url.toString();
    } catch {
      return undefined;
    }
  }

  get hasLicenses(): boolean {
    return this.device?.deviceManagementFeatures?.hasLicenses === true;
  }

  get deviceImageSrc(): string | undefined {
    const normalizedModel = this.deviceImageModel?.trim().toLowerCase();

    return this.deviceImageByModel.find((mapping) =>
      mapping.models.some((model) => normalizedModel?.includes(model)),
    )?.src;
  }

  get deviceImageModel(): string | undefined {
    const details = this.device?.details;

    return (
      this.getSummaryModel(this.device) ||
      (this.isAircraftDeviceDetails(details)
        ? details.aircraftModel?.name?.trim()
        : undefined) ||
      details?.type ||
      this.device?.deviceType?.trim() ||
      undefined
    );
  }

  editDevice(): void {
    if (!this.device) {
      return;
    }

    const dialogRef = this.dialog.open(EditDeviceModalComponent, {
      data: { device: this.device },
    });

    dialogRef.closed.subscribe((result) => {
      if (result && this.deviceId) {
        this.loadDevice(this.deviceId);
      }
    });
  }

  refreshDeviceData(): void {
    if (!this.deviceId) {
      return;
    }

    this.loadDevice(this.deviceId);
  }

  backToResourcesList(): void {
    this.router.navigateByUrl(this.resourcesReturnUrl);
  }

  get resourcesReturnUrl(): string {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    return returnTo && this.allowedResourceReturnUrls.has(returnTo)
      ? returnTo
      : '/resources';
  }

  get resourcesReturnLabel(): string {
    return this.resourcesReturnUrl === '/admin/all-resources'
      ? this.translate.instant('all_resources.title')
      : this.translate.instant('resources.title');
  }

  private loadDevice(deviceId: string): void {
    this.deviceService
      .getDevice(deviceId, 'FULL')
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.backToResourcesList();
          return of(undefined);
        }),
      )
      .subscribe((device) => {
        if (!device) {
          return;
        }

        this.device = device;
        this.deviceCompanyId = device.company?.id?.trim() || null;
        this.linkedLicenseBatches = device.licenseBatches ?? [];
        this.generalInfoItems = this.resolveGeneralInfoItems(device);
        this.devicePropertyItems = this.resolveDevicePropertyItems(device);
      });
  }

  private resolveDevicePropertyItems(device: DeviceDto): GeneralInfoItem[] {
    const endUserProperties = device.properties?.['endUserProperties'];
    if (
      !endUserProperties ||
      typeof endUserProperties !== 'object' ||
      Array.isArray(endUserProperties)
    ) {
      return [];
    }

    return Object.entries(endUserProperties).map(([key, value]) => ({
      label: key,
      value: this.formatPropertyValue(value),
    }));
  }

  private formatPropertyValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  private resolveGeneralInfoItems(device: DeviceDto): GeneralInfoItem[] {
    const summaryInfoItems = this.resolveDeviceSummaryInfoItems(device);
    if (summaryInfoItems.length) {
      return this.withDescriptionInfoItem(summaryInfoItems, device.description);
    }

    const generalInfoItems = (device.generalInfoItems ?? []).filter(
      (item) => !!item.label?.trim() || !!item.value?.trim(),
    );

    if (generalInfoItems.length) {
      return this.withDescriptionInfoItem(generalInfoItems, device.description);
    }

    if (!this.isAircraftDeviceDetails(device.details)) {
      return this.withDescriptionInfoItem(
        [
          {
            label: this.translate.instant('Device_details.device_id'),
            value: device.deviceId ?? 'n/a',
          },
          {
            label: this.translate.instant('Device_details.device_type'),
            value: device.details?.type ?? device.deviceType ?? 'n/a',
          },
          {
            label: this.translate.instant('resources.table.model'),
            value: this.getSummaryModel(device) || 'n/a',
          },
          {
            label: this.translate.instant('resources.table.unit_id'),
            value: this.getSummaryUnitId(device) || 'n/a',
          },
        ],
        device.description,
      );
    }

    return this.withDescriptionInfoItem(
      [
        {
          label: this.translate.instant('Drone_details.unit_number'),
          value:
            this.getSummaryUnitId(device) ||
            String(device.details.unitId ?? 'n/a'),
        },
        {
          label: this.translate.instant('Drone_details.qos_version'),
          value: device.details.software?.qosVersion ?? 'n/a',
        },
        {
          label: this.translate.instant('Drone_details.autopilot_serial'),
          value: device.details.autopilot?.serialNumber ?? 'n/a',
        },
      ],
      device.description,
    );
  }

  private withDescriptionInfoItem(
    items: GeneralInfoItem[],
    description?: string,
  ): GeneralInfoItem[] {
    const hasDescription = items.some(
      (item) => item.label?.trim().toLowerCase() === 'description',
    );

    if (hasDescription) {
      return items;
    }

    return [
      ...items,
      {
        label: this.translate.instant('Device_details.description'),
        value: description?.trim() || 'n/a',
      },
    ];
  }

  private isQsUavDetails(
    details?: DeviceDetailsDto,
  ): details is QsUavDetailsDto {
    return details?.type === 'QsUav';
  }

  private isQsTrinityProDetails(
    details?: DeviceDetailsDto,
  ): details is QsTrinityProDetailsDto {
    return details?.type === 'QsTrinityPro';
  }

  private isAircraftDeviceDetails(
    details?: DeviceDetailsDto,
  ): details is QsUavDetailsDto | QsTrinityProDetailsDto {
    return this.isQsUavDetails(details) || this.isQsTrinityProDetails(details);
  }

  private getSummaryModel(device?: DeviceDto): string {
    return (
      device?.deviceSummary?.model?.trim() ||
      device?.deviceSummary?.modelName?.trim() ||
      ''
    );
  }

  private getSummaryUnitId(device?: DeviceDto): string {
    return device?.deviceSummary?.unitId?.trim() || '';
  }

  private resolveDeviceSummaryInfoItems(device: DeviceDto): GeneralInfoItem[] {
    const items: GeneralInfoItem[] = [];
    const summaryModel = this.getSummaryModel(device);
    const summaryUnitId = this.getSummaryUnitId(device);

    if (summaryUnitId) {
      items.push({
        label: this.translate.instant('Drone_details.unit_number'),
        value: summaryUnitId,
      });
    }

    if (summaryModel) {
      items.push({
        label: this.translate.instant('resources.table.model'),
        value: summaryModel,
      });
    }

    return [...items, ...this.resolveDeviceSummaryFacts(device)];
  }

  private resolveDeviceSummaryFacts(device: DeviceDto): GeneralInfoItem[] {
    return (device.deviceSummary?.facts ?? [])
      .filter((fact) => !!fact.label?.trim())
      .map((fact) => ({
        label: fact.label?.trim(),
        value: fact.value?.trim() || 'n/a',
      }));
  }
}
